import { createSignal } from 'solid-js'
import type { MetricDefinition } from '@fasting-timer/shared'

import { useI18n } from '../../i18n'
import {
   addMeasurement,
   measurementStatus,
   setMeasurementStatusMessage,
} from '../../state/measurementsStore'
import { unitSystem } from '../../state/unitSystemStore'
import { getExampleValue, parseDurationSeconds, parseQuantity } from '../../utils/units'
import DurationInput from '../ui/DurationInput'
import StatusMessage from '../ui/StatusMessage'
import TextInput from '../ui/TextInput'
import { distanceUnitLabel } from './measurementFormat'

type MetricEntryFormProps = {
   definition: MetricDefinition
}

/**
 * Renders the distance + duration form that logs a new entry for a metric.
 *
 * @param props - The metric definition.
 * @returns The form element.
 */
const MetricEntryForm = (props: MetricEntryFormProps) => {
   const t = useI18n()
   const [distanceInput, setDistanceInput] = createSignal('')
   const [durationInput, setDurationInput] = createSignal('')

   /**
    * Parses the inputs, validates through the store, and clears the form on success.
    *
    * @param event - The submit event.
    */
   const handleSubmit = (event: SubmitEvent) => {
      event.preventDefault()
      const distance = parseQuantity('distance', distanceInput(), unitSystem())
      const duration = parseDurationSeconds(durationInput())
      if (distance === null || duration === null) {
         setMeasurementStatusMessage(t('Enter a distance and a duration.'), true)
         return
      }
      const entry = addMeasurement(props.definition.id, { distance, duration })
      if (entry) {
         setDistanceInput('')
         setDurationInput('')
      }
   }

   return (
      <form id="measurement-form" class="space-y-3" onSubmit={handleSubmit}>
         <h2 class="text-xl font-semibold text-ink">
            {t('Log {metric}', { metric: t(props.definition.labelKey) })}
         </h2>
         <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2">
            <label class="block">
               <span class="mb-1 block text-xs text-ink-muted">
                  {t('Distance ({unit})', { unit: distanceUnitLabel() })}
               </span>
               <TextInput
                  id="measurement-distance-input"
                  type="number"
                  step="0.01"
                  min="0"
                  inputmode="decimal"
                  value={distanceInput()}
                  onInput={event => setDistanceInput(event.currentTarget.value)}
                  placeholder={t('e.g., {value}', {
                     value: getExampleValue('distance', unitSystem()),
                  })}
               />
            </label>
            <label class="block">
               <span class="mb-1 block text-xs text-ink-muted">{t('Time (m:ss)')}</span>
               <DurationInput
                  id="measurement-duration-input"
                  value={durationInput()}
                  onValueInput={setDurationInput}
               />
            </label>
            <button
               id="measurement-submit-btn"
               type="submit"
               class="btn-accent px-4 py-3 text-white font-semibold rounded-lg transition sm:min-w-[80px]"
            >
               {t('Add')}
            </button>
         </div>
         <StatusMessage id="measurement-status" status={measurementStatus()} />
      </form>
   )
}

export default MetricEntryForm
